import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Pencil, Trash2, Loader2, Gem, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

const METAL_TYPES = ['Gold', 'Silver', 'Platinum', 'Palladium', 'Other'];
const FORMS = ['Coins', 'Bars', 'Rounds', 'Notes', 'Bullion', 'Other'];
const WEIGHT_UNITS = ['oz', 'g', 'kg', 'dwt'];

function MetalDialog({ open, onOpenChange, metal, companyId, onSave }) {
  const [form, setForm] = useState({
    item_id: '', name: '', serial_number: '', collection: '',
    market_value: '', grader: '', grade: '', cert_number: '',
    weight: '', weight_unit: 'oz', metal_type: 'Gold',
    form_type: 'Coins', purity: '', quantity: '1',
    purchase_date: '', purchase_price: '', storage_location: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (metal) {
        const meta = parseMeta(metal.notes);
        setForm({
          item_id: meta.item_id || '', name: metal.name || '',
          serial_number: meta.serial_number || '', collection: metal.institution || '',
          market_value: metal.value != null ? String(metal.value) : '',
          grader: meta.grader || '', grade: meta.grade || '', cert_number: meta.cert_number || metal.account_number || '',
          weight: meta.weight || '', weight_unit: meta.weight_unit || 'oz',
          metal_type: meta.metal_type || 'Gold', form_type: meta.form_type || 'Coins',
          purity: meta.purity || '', quantity: meta.quantity || '1',
          purchase_date: meta.purchase_date || '', purchase_price: meta.purchase_price != null ? String(meta.purchase_price) : '',
          storage_location: meta.storage_location || '', notes: meta.notes_text || ''
        });
      } else {
        setForm({ item_id: '', name: '', serial_number: '', collection: '', market_value: '', grader: '', grade: '', cert_number: '', weight: '', weight_unit: 'oz', metal_type: 'Gold', form_type: 'Coins', purity: '', quantity: '1', purchase_date: '', purchase_price: '', storage_location: '', notes: '' });
      }
    }
  }, [metal, open]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        company_id: companyId, name: form.name, asset_type: 'metal',
        value: parseFloat(form.market_value) || 0,
        institution: form.collection || null,
        account_number: form.cert_number ? `${form.grader} ${form.cert_number}`.trim() : form.serial_number || null,
        notes: JSON.stringify({
          item_id: form.item_id, serial_number: form.serial_number,
          grader: form.grader, grade: form.grade, cert_number: form.cert_number,
          weight: form.weight, weight_unit: form.weight_unit,
          metal_type: form.metal_type, form_type: form.form_type,
          purity: form.purity, quantity: form.quantity,
          purchase_date: form.purchase_date,
          purchase_price: parseFloat(form.purchase_price) || 0,
          storage_location: form.storage_location, notes_text: form.notes
        }),
        tags: [form.collection].filter(Boolean)
      };
      if (metal) {
        await axios.put(`${API}/api/assets/${metal.id}`, payload, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/api/assets`, payload, { withCredentials: true });
        toast.success('Added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{metal ? 'Edit Precious Metal' : 'Add Precious Metal'}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {/* Row 1 */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Item ID</Label><Input value={form.item_id} onChange={e => setForm(p => ({...p, item_id: e.target.value}))} placeholder="Your custom ID or SKU" data-testid="metal-item-id" /></div>
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g., 2023 American Gold Eagle" data-testid="metal-name" /></div>
          </div>
          {/* Row 2 */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Serial Number</Label><Input value={form.serial_number} onChange={e => setForm(p => ({...p, serial_number: e.target.value}))} placeholder="Serial or certification number" data-testid="metal-serial" /></div>
            <div><Label>Collection</Label><Input value={form.collection} onChange={e => setForm(p => ({...p, collection: e.target.value}))} placeholder="e.g., American Eagles, Maple Leafs" data-testid="metal-collection" /></div>
          </div>
          {/* Row 3 */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Market Value *</Label><Input type="number" value={form.market_value} onChange={e => setForm(p => ({...p, market_value: e.target.value}))} placeholder="0" data-testid="metal-value" /></div>
            <div><Label>Grader</Label><Input value={form.grader} onChange={e => setForm(p => ({...p, grader: e.target.value}))} placeholder="e.g., PCGS, NGC, ANACS" data-testid="metal-grader" /></div>
          </div>
          {/* Row 4 */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Grade (Leave blank for ungraded)</Label><Input value={form.grade} onChange={e => setForm(p => ({...p, grade: e.target.value}))} placeholder="e.g., MS70, PR69" data-testid="metal-grade" /></div>
            <div><Label>Number</Label><Input value={form.cert_number} onChange={e => setForm(p => ({...p, cert_number: e.target.value}))} placeholder="Certification/reference number" data-testid="metal-cert" /></div>
          </div>
          {/* Row 5 - Weight & Metal Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Weight *</Label>
              <div className="flex gap-2">
                <Input type="number" step="0.01" value={form.weight} onChange={e => setForm(p => ({...p, weight: e.target.value}))} placeholder="0" className="flex-1" data-testid="metal-weight" />
                <Select value={form.weight_unit} onValueChange={v => setForm(p => ({...p, weight_unit: v}))}>
                  <SelectTrigger className="w-20" data-testid="metal-weight-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEIGHT_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Metal Type</Label>
              <Select value={form.metal_type} onValueChange={v => setForm(p => ({...p, metal_type: v}))}>
                <SelectTrigger data-testid="metal-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{METAL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* Row 6 - Form & Purity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Form</Label>
              <Select value={form.form_type} onValueChange={v => setForm(p => ({...p, form_type: v}))}>
                <SelectTrigger data-testid="metal-form-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Purity</Label><Input value={form.purity} onChange={e => setForm(p => ({...p, purity: e.target.value}))} placeholder="e.g., .999, 24k" data-testid="metal-purity" /></div>
          </div>
          {/* Row 7 - Quantity & Purchase Date */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={e => setForm(p => ({...p, quantity: e.target.value}))} placeholder="1" data-testid="metal-quantity" /></div>
            <div><Label>Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={e => setForm(p => ({...p, purchase_date: e.target.value}))} data-testid="metal-purchase-date" /></div>
          </div>
          {/* Row 8 - Purchase Price & Storage */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Purchase Price</Label><Input type="number" value={form.purchase_price} onChange={e => setForm(p => ({...p, purchase_price: e.target.value}))} placeholder="0" data-testid="metal-purchase-price" /></div>
            <div><Label>Storage Location</Label><Input value={form.storage_location} onChange={e => setForm(p => ({...p, storage_location: e.target.value}))} placeholder="e.g., Home Safe, Bank Vault" data-testid="metal-storage" /></div>
          </div>
          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Additional notes..."
              className="w-full min-h-[60px] rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y" data-testid="metal-notes" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-metal-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : metal ? 'Update' : 'Add Precious Metal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseMeta(notesStr) {
  try { return JSON.parse(notesStr); } catch { return {}; }
}

export default function PreciousMetalsPage() {
  const { currentCompany } = useCompany();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  const fetchItems = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/assets`, { params: { company_id: currentCompany.id, asset_type: 'metal' }, withCredentials: true });
      setItems(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/assets/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Deleted'); setDeleteTarget(null); fetchItems();
    } catch { toast.error('Failed'); }
  };

  const filtered = items.filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    const meta = parseMeta(item.notes);
    return (item.name || '').toLowerCase().includes(q) ||
      (item.institution || '').toLowerCase().includes(q) ||
      (item.account_number || '').toLowerCase().includes(q) ||
      (meta.grader || '').toLowerCase().includes(q) ||
      (meta.grade || '').toLowerCase().includes(q) ||
      (meta.metal_type || '').toLowerCase().includes(q) ||
      (meta.collection || '').toLowerCase().includes(q);
  });

  const totalValue = items.reduce((s, i) => s + (i.value || 0), 0);
  const gradedItems = items.filter(i => { const m = parseMeta(i.notes); return m.grade && m.grade.trim(); });
  const ungradedItems = items.filter(i => { const m = parseMeta(i.notes); return !m.grade || !m.grade.trim(); });
  const gradedValue = gradedItems.reduce((s, i) => s + (i.value || 0), 0);
  const ungradedValue = ungradedItems.reduce((s, i) => s + (i.value || 0), 0);
  const [viewFilter, setViewFilter] = useState('all');
  const [sortBy, setSortBy] = useState('collection');
  const [typeFilter, setTypeFilter] = useState('all');

  if (!currentCompany) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;

  const getFilteredItems = () => {
    let result = filtered;
    if (viewFilter === 'graded') result = result.filter(i => { const m = parseMeta(i.notes); return m.grade && m.grade.trim(); });
    if (viewFilter === 'ungraded') result = result.filter(i => { const m = parseMeta(i.notes); return !m.grade || !m.grade.trim(); });
    if (typeFilter !== 'all') result = result.filter(i => { const m = parseMeta(i.notes); return (m.metal_type || '').toLowerCase() === typeFilter; });
    if (sortBy === 'collection') result = [...result].sort((a, b) => (a.institution || '').localeCompare(b.institution || ''));
    else if (sortBy === 'value') result = [...result].sort((a, b) => (b.value || 0) - (a.value || 0));
    else if (sortBy === 'name') result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return result;
  };
  const displayItems = getFilteredItems();

  return (
    <div className="space-y-5" data-testid="precious-metals-page">
      {/* Header with toolbar */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Precious Metals</h1>
            <p className="text-gray-500 text-sm mt-0.5">Track your precious metal investments</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[155px] h-9 text-sm" data-testid="metal-sort"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="collection">Sort by Collection</SelectItem>
                <SelectItem value="value">Sort by Value</SelectItem>
                <SelectItem value="name">Sort by Name</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="metal-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="platinum">Platinum</SelectItem>
                <SelectItem value="palladium">Palladium</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setAddOpen(true)} data-testid="add-metal-btn">
              <Plus className="w-4 h-4 mr-2" />Add Precious Metal
            </Button>
          </div>
        </div>
      </div>

      {/* Three colored stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
          <p className="text-amber-100 text-sm font-medium">Total Value</p>
          <p className="text-3xl font-bold mt-1" data-testid="total-metals-value">{fmtCurrency(totalValue)}</p>
          <p className="text-amber-200 text-sm mt-1">{items.length} items</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <p className="text-blue-100 text-sm font-medium">Graded Metals</p>
          <p className="text-3xl font-bold mt-1">{fmtCurrency(gradedValue)}</p>
          <p className="text-blue-200 text-sm mt-1">{gradedItems.length} items</p>
        </div>
        <div className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl p-5 text-white">
          <p className="text-slate-300 text-sm font-medium">Ungraded Metals</p>
          <p className="text-3xl font-bold mt-1">{fmtCurrency(ungradedValue)}</p>
          <p className="text-slate-400 text-sm mt-1">{ungradedItems.length} items</p>
        </div>
      </div>

      {/* Tabs: All / Graded / Ungraded */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {[
          { key: 'all', label: `All Metals (${items.length})` },
          { key: 'graded', label: `Graded (${gradedItems.length})` },
          { key: 'ungraded', label: `Ungraded (${ungradedItems.length})` },
        ].map(tab => (
          <button key={tab.key} onClick={() => { setViewFilter(tab.key); setPage(1); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${viewFilter === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search metals, grader, grade, collection..."
          className="pl-9" data-testid="metal-search" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Collection</TableHead>
                <TableHead>Grader</TableHead>
                <TableHead>Grade</TableHead>
                <TableHead>Cert #</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayItems.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400">
                  <Gem className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                  <p className="font-medium">{search ? 'No results found' : 'No precious metals yet'}</p>
                </TableCell></TableRow>
              ) : displayItems.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(item => {
                const meta = parseMeta(item.notes);
                return (
                  <TableRow key={item.id} className="hover:bg-gray-50/50" data-testid={`metal-row-${item.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <Gem className="w-3.5 h-3.5 text-amber-600" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[250px]">{item.name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{item.institution || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{meta.grader || '-'}</TableCell>
                    <TableCell>
                      {meta.grade ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">{meta.grade}</span> : <span className="text-gray-400 text-sm">-</span>}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 font-mono">{meta.cert_number || '-'}</TableCell>
                    <TableCell className="text-right font-semibold text-gray-900">{fmtCurrency(item.value)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditItem(item)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50" data-testid={`edit-metal-${item.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteTarget(item)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-metal-${item.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Showing {Math.min((page - 1) * ITEMS_PER_PAGE + 1, displayItems.length)}–{Math.min(page * ITEMS_PER_PAGE, displayItems.length)} of {displayItems.length}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="metals-prev-page">
              Previous
            </Button>
            <span className="text-sm text-gray-600 px-2">Page {page} of {Math.ceil(displayItems.length / ITEMS_PER_PAGE) || 1}</span>
            <Button variant="outline" size="sm" disabled={page * ITEMS_PER_PAGE >= displayItems.length} onClick={() => setPage(p => p + 1)} data-testid="metals-next-page">
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <MetalDialog open={addOpen || !!editItem} onOpenChange={v => { if (!v) { setAddOpen(false); setEditItem(null); } }}
        metal={editItem} companyId={currentCompany?.id} onSave={fetchItems} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Item</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.name}"? This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
